import { HttpClient } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { downgradeInjectable } from '@angular/upgrade/static'
import { Estimate } from '@tradecafe/types/core'
import { AngularCopy } from 'src/decorators/angular-copy.decorator'
import { AngularParams, HaveAngularParams } from 'src/decorators/angular-params.decorator'
import { environment } from 'src/environments/environment'
import { ElasticSearchPayload, FilterDataSelectOption } from 'src/services/elastic-search'

/**
 * A single hit returned by the search-service `POST /estimates/search` endpoint.
 * The estimate document itself is under `doc`; supplier/buyer/product/creator
 * are resources resolved by the search service alongside it.
 */
export interface EstimateSearchHit {
  doc: Estimate
  supplier?: { account: string, name: string }
  buyer?: { account: string, name: string }
  product?: { product_id: string, name: string }
  creator?: { user_id: string, firstname?: string, lastname?: string, fullname?: string }
  index_updated?: number
}

export interface EstimateSearchResponse {
  total: number
  hits: EstimateSearchHit[]
  totals?: Dictionary<unknown>
}

/** Aggregated filter options returned by `POST /estimates/filters`. */
export interface EstimateFiltersResponse {
  supplier?: FilterDataSelectOption[]
  buyer?: FilterDataSelectOption[]
  product?: FilterDataSelectOption[]
  owner?: FilterDataSelectOption[]
  origin_country?: string[]
  dest_country?: string[]
  estimate_type?: string[]
  created?: { min: number, max: number }
  updated?: { min: number, max: number }
  [key: string]: unknown
}


const { apiUrl } = environment


export default module =>
  module.service('EstimateApi', downgradeInjectable(EstimateApiService))


@Injectable()
@AngularCopy()
export class EstimateApiService {
  private http = inject(HttpClient)


  @HaveAngularParams()
  list(@AngularParams() params) {
    return this.http.get<{ data: Estimate[] }>(`${apiUrl}/estimates`, { params }).toPromise()
  }

  create(data: Partial<Estimate>) {
    return this.http.post<{ data: Estimate }>(`${apiUrl}/estimates`, data).toPromise()
  }

  get(id: string) {
    return this.http.get<{ data: Estimate }>(`${apiUrl}/estimates/${id}`).toPromise()
  }

  update(id: string, data: Partial<Estimate>) {
    return this.http.put<{ data: Estimate }>(`${apiUrl}/estimates/${id}`, data).toPromise()
  }

  delete(id: string) {
    return this.http.delete<{ data: Estimate }>(`${apiUrl}/estimates/${id}`).toPromise()
  }

  search(payload: ElasticSearchPayload) {
    return this.http.post<EstimateSearchResponse>(`${apiUrl}/estimates/search`, payload).toPromise()
  }

  filters(payload: ElasticSearchPayload) {
    return this.http.post<EstimateFiltersResponse>(`${apiUrl}/estimates/filters`, payload).toPromise()
  }
}
